export const RULE_TYPE = "AMAZON_CAMPAIGN_ABO";
export const EXECUTE_RULE_TYPE = "AMAZON_CAMPAIGN_ABO_RULE";

/** Log API `type` filter — differs from automation-rule-set `type`. */
export const RULE_LOG_TYPE = "AMAZON_CAMPAIGN_ABO_RULE";

export const STATUS_FILTERS = ["All", "Enabled", "Paused"];

export const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
export const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export const DAY_NAMES_DISPLAY = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** Dayparting slot start: 00:00 through 23:00 */
export const SLOT_START_HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = i.toString().padStart(2, "0");
  return { value: `${h}:00`, label: `${h}:00` };
});

/** Dayparting slot end: 01:00 through 24:00 */
export const SLOT_END_HOURS = Array.from({ length: 24 }, (_, i) => {
  const hour = i + 1;
  const label = hour === 24 ? "24:00" : `${hour.toString().padStart(2, "0")}:00`;
  return { value: label, label };
});

export const BUDGET_FAMILY = {
  SET: "SET",
  ALLOCATE: "ALLOCATE",
};

export const SET_BUDGET_FAMILY_TOOLTIP =
  "Change each slot’s budget independently using a number, % increase, or % decrease from the campaign daily budget. Min and max budget limits still apply.";

export const ALLOCATE_BUDGET_FAMILY_TOOLTIP =
  "Split each campaign’s daily budget across time slots. Use % of Daily Budget (all slots combined must be 100% or less) or Rest of Daily Budget on the last slot. Min budget is locked to the supported minimum and max budget is locked to daily budget.";

export const BUDGET_FAMILY_OPTIONS = [
  { value: BUDGET_FAMILY.SET, label: "Set", tooltip: SET_BUDGET_FAMILY_TOOLTIP },
  { value: BUDGET_FAMILY.ALLOCATE, label: "Allocate", tooltip: ALLOCATE_BUDGET_FAMILY_TOOLTIP },
];

export const BUDGET_CHANGE_TYPE = {
  ABSOLUTE: "ABSOLUTE",
  PERCENT_INCREASE: "PERCENT_INCREASE",
  PERCENT_DECREASE: "PERCENT_DECREASE",
  PERCENT_OF_ALLOCATED_BUDGET: "PERCENT_OF_ALLOCATED_BUDGET",
  REST_OF_ALLOCATED_BUDGET: "REST_OF_ALLOCATED_BUDGET",
};

export const SET_BUDGET_TYPE_OPTIONS = [
  { value: BUDGET_CHANGE_TYPE.ABSOLUTE, label: "Number" },
  { value: BUDGET_CHANGE_TYPE.PERCENT_INCREASE, label: "% Increase" },
  { value: BUDGET_CHANGE_TYPE.PERCENT_DECREASE, label: "% Decrease" },
];

export const ALLOCATE_BUDGET_TYPE_OPTIONS = [
  { value: BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET, label: "% of Daily Budget" },
  {
    value: BUDGET_CHANGE_TYPE.REST_OF_ALLOCATED_BUDGET,
    label: "Rest of Daily Budget",
    hint: "Last slot only",
    lastSlotOnly: true,
  },
];

export const BUDGET_TYPE_OPTIONS = [
  ...SET_BUDGET_TYPE_OPTIONS,
  ...ALLOCATE_BUDGET_TYPE_OPTIONS,
];

export function isAllocateChangeType(changeType) {
  return (
    changeType === BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET ||
    changeType === BUDGET_CHANGE_TYPE.REST_OF_ALLOCATED_BUDGET
  );
}

export function isRestOfAllocatedBudgetType(changeType) {
  return changeType === BUDGET_CHANGE_TYPE.REST_OF_ALLOCATED_BUDGET;
}

export function isPercentBudgetValueType(changeType) {
  return (
    changeType === BUDGET_CHANGE_TYPE.PERCENT_INCREASE ||
    changeType === BUDGET_CHANGE_TYPE.PERCENT_DECREASE ||
    changeType === BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET ||
    changeType === BUDGET_CHANGE_TYPE.REST_OF_ALLOCATED_BUDGET
  );
}

export function defaultChangeTypeForBudgetFamily(budgetFamily) {
  return budgetFamily === BUDGET_FAMILY.ALLOCATE
    ? BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET
    : BUDGET_CHANGE_TYPE.PERCENT_INCREASE;
}

export function getBudgetTypeLabel(changeType) {
  return BUDGET_TYPE_OPTIONS.find((option) => option.value === changeType)?.label ?? "";
}

/** Slot duration status when min/max budget limits are reached. */
export const SLOT_STATUS_OPTIONS = [
  {
    value: "active",
    menuLabel: "Enabled (Continue at Min/Max Budget)",
  },
  {
    value: "pause",
    menuLabel: "Paused (At Min/Max Budget)",
  },
];

export const GAP_OPTIONS = [
  { value: "USE_DEFAULT_BUDGET", label: "Use Daily Budget" },
  { value: "PAUSE_CAMPAIGN", label: "Pause Campaign" },
];

export const CAMPAIGN_SELECT_ALL_ID = "__campaign_abo_select_all__";
export const CAMPAIGN_FETCH_LIMIT = 150;
export const CAMPAIGN_TABLE_PAGE_SIZE = 25;
export const CAMPAIGN_SELECT_ALL_LABEL = "Select All";

/** INR fallback only. Prefer getMinCampaignBudget() from helpers (currency-wise). */
export const MIN_CAMPAIGN_BUDGET = 50;

export const GAP_HANDLING_SECTION_TOOLTIP =
  "Set campaign behavior for time periods outside scheduled slots.";

export const DAILY_COMPOUNDING_TOOLTIP =
  "Applies each day's budget change on top of the previous day's adjusted amount for all selected campaigns.";

export const PERFORMANCE_FILTERS_TOOLTIP =
  "When enabled, each slot duration shows a filter icon. Use it to set performance conditions (e.g. ROAS, spend, clicks) for that slot. The budget change applies only when campaigns meet those conditions for the selected lookback period.";

export const PERF_FILTER_IMPACT_HINT =
  "Campaigns marked with a star match the performance filter for this slot.";

export const ALL_DAYS_CUSTOM_DAYS_TOOLTIP =
  "Use when budget should change by calendar dates. Set your rule start/end dates, pick all days or specific weekdays, then add slot durations with their own date ranges inside that period.";

export const DAYPARTING_TOOLTIP =
  "Use when budget should change by time of day. Set your rule start/end dates, pick all days or specific weekdays, then add slot durations with start/end times (e.g. 09:00–18:00). Those hours apply on each selected day within the rule period.";

export const AUTOMATION_MODE_OPTIONS = [
  {
    value: "ALL_DAYS_CUSTOM_DAYS",
    label: "All Days / Custom Days",
    tooltip: ALL_DAYS_CUSTOM_DAYS_TOOLTIP,
  },
  {
    value: "DAYPARTING",
    label: "Dayparting",
    tooltip: DAYPARTING_TOOLTIP,
  },
];

export const HEADING_TITLE_COLOR = "#1C1C1E";

export const DEFAULT_GUARD_RAILS = {
  respect_min_budget: true,
  respect_max_budget: true,
  fallback_to_base_budget_on_missing_data: true,
};
